import type { GridApi } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { ItemsApi } from '@/lib/api/common/items';
import type { ItemDependency } from '@/lib/api/types/items';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { buildIdKeyMatcher } from '../hooks/buildIdKeyMatcher';
import { useCrossCachePagesSync } from '../hooks/useCrossCachePagesSync';
import type { DatasetPage } from '../hooks/useDatasetPages';
import { useTablePagedQuery } from '../hooks/useTablePagedQuery';

const EMPTY_DEPENDENCIES: ReadonlyMap<number, ItemDependency[]> = new Map();
const matchDependenciesKey = buildIdKeyMatcher(commonQueryKeys.dependencies.byItemId);
const dependenciesFromSource = (_id: number, sourceData: unknown): ItemDependency[] | undefined =>
  sourceData as ItemDependency[] | undefined;

type VisibleDependenciesContextType = {
  dependenciesById: ReadonlyMap<number, ItemDependency[]>;
};

const VisibleDependenciesContext = React.createContext<VisibleDependenciesContextType | undefined>(
  undefined,
);

export const useVisibleDependencies = (id: number | undefined): ItemDependency[] | undefined => {
  const context = React.useContext(VisibleDependenciesContext);
  if (!context || id === undefined) return undefined;
  return context.dependenciesById.get(id);
};

export const VisibleDependenciesProvider = ({
  pages,
  visiblePageIndices,
  pageIndexById,
  datasetSignature,
  apiRef,
  instanceKey,
  enabled = true,
  children,
}: {
  pages: readonly DatasetPage[];
  visiblePageIndices: readonly number[];
  pageIndexById: ReadonlyMap<number, number>;
  datasetSignature: string;
  apiRef: React.RefObject<GridApi>;
  instanceKey: string;
  enabled?: boolean;
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();

  const fetchByIds = React.useCallback(
    async (ids: readonly number[]) => {
      const items = await ItemsApi.getItemsBulkFetch({ ids: [...ids], fields: ['DEPENDENCIES'] });
      const itemsById = new Map(items.map((item) => [item.id, item]));
      const dependenciesById = new Map<number, ItemDependency[]>();
      for (const id of ids) {
        const dependencies = itemsById.get(id)?.dependencies ?? [];
        dependenciesById.set(id, dependencies);
        queryClient.setQueryData(commonQueryKeys.dependencies.byItemId(id), dependencies);
      }
      return dependenciesById;
    },
    [queryClient],
  );

  const { loadedById } = useTablePagedQuery<ItemDependency[]>({
    providerName: commonQueryKeys.itemsTable.visibleDependencies,
    instanceKey,
    pages,
    visiblePageIndices,
    pageIndexById,
    datasetSignature,
    fetchByIds,
    enabled,
    apiRef,
  });

  useCrossCachePagesSync<ItemDependency[]>({
    providerName: commonQueryKeys.itemsTable.visibleDependencies,
    instanceKey,
    pageIndexById,
    datasetSignature,
    matchKey: matchDependenciesKey,
    transform: dependenciesFromSource,
  });

  const value = React.useMemo<VisibleDependenciesContextType>(
    () => ({ dependenciesById: loadedById.size > 0 ? loadedById : EMPTY_DEPENDENCIES }),
    [loadedById],
  );

  return (
    <VisibleDependenciesContext.Provider value={value}>
      {children}
    </VisibleDependenciesContext.Provider>
  );
};
